<template>
  <div class="charge-chart" :class="{ 'with-card': showCard }">
    <el-card v-if="showCard" shadow="never">
      <div slot="header">
        <span>{{ title }}</span>
      </div>
      <el-tabs v-model="activeTab" type="card" @tab-click="handleTabClick">
        <el-tab-pane label="电量" name="totalSoc" />
        <el-tab-pane label="最大压差" name="volDiff" />
        <el-tab-pane label="最大温差" name="tempDiff" />
      </el-tabs>
      <Chart ref="chargeChart" :option="chartOption" :height="height" :onLoad="onChartLoad" />
    </el-card>
    <template v-else>
      <el-tabs v-model="activeTab" type="card" class="chart-tabs" @tab-click="handleTabClick">
        <el-tab-pane label="电量" name="totalSoc" />
        <el-tab-pane label="最大压差" name="volDiff" />
        <el-tab-pane label="最大温差" name="tempDiff" />
      </el-tabs>
      <Chart ref="chargeChart" :option="chartOption" :height="height" :onLoad="onChartLoad" />
    </template>
  </div>
</template>

<script>
import Chart from "@/components/Chart";
import { DateUtil } from "@/util";

export default {
  name: "ChargeChart",
  components: {
    Chart
  },
  props: {
    chargeInfos: {
      type: Array,
      default: () => []
    },
    showCard: {
      type: Boolean,
      default: true
    },
    title: {
      type: String,
      default: "电量变化"
    },
    height: {
      type: Number,
      default: 200
    }
  },
  data() {
    return {
      chart: null,
      activeTab: "totalSoc"
    };
  },
  computed: {
    chartOption() {
      const sortedInfos = this.chargeInfos && this.chargeInfos.length
        ? [...this.chargeInfos]
        : [];
      const times = sortedInfos.map((item) =>
        DateUtil.dateStrFormat(item.createTime, "hh:mm")
      );

      const tabConfig = {
        totalSoc: {
          name: "电量",
          unit: "%",
          yName: "电量(%)",
          min: 0,
          max: 100,
          formatter: (v) => `${v}%`,
          getData: (item) => item.totalSoc != null ? item.totalSoc : null,
          barColor: (value) => {
            if (value >= 80) return "#67C23A";
            if (value >= 50) return "#E6A23C";
            if (value >= 20) return "#F56C6C";
            return "#909399";
          }
        },
        volDiff: {
          name: "最大压差",
          unit: "mV",
          yName: "最大压差(mV)",
          min: null,
          max: null,
          formatter: (v) => `${v} mV`,
          getData: (item) => item.volDiff != null ? item.volDiff : null,
          barColor: () => "#409EFF"
        },
        tempDiff: {
          name: "最大温差",
          unit: "℃",
          yName: "最大温差(℃)",
          min: null,
          max: 20,
          formatter: (v) => `${v} ℃`,
          getData: (item) => item.tempDiff != null ? item.tempDiff : null,
          barColor: () => "#E6A23C"
        }
      };

      const config = tabConfig[this.activeTab] || tabConfig.totalSoc;
      const data = sortedInfos.map(config.getData);

      const emptyOption = {
        tooltip: { trigger: "axis" },
        xAxis: { type: "category", data: [] },
        yAxis: { type: "value", name: config.yName },
        series: [{ name: config.name, type: "bar", data: [] }]
      };

      if (sortedInfos.length === 0) {
        return emptyOption;
      }

      const yAxisOption = {
        type: "value",
        name: config.yName,
        axisLabel: config.formatter ? { formatter: "{value} " + config.unit } : {}
      };
      if (config.min != null) yAxisOption.min = config.min;
      if (config.max != null) yAxisOption.max = config.max;

      return {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          formatter: (params) => {
            const param = params[0];
            const idx = param.dataIndex;
            const v = data[idx];
            return `${param.name}<br/>${config.name}: ${v != null ? config.formatter(v) : "—"}`;
          }
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          containLabel: true
        },
        xAxis: {
          type: "category",
          data: times,
          axisLabel: {
            rotate: 45,
            interval: Math.floor(times.length / 10) || 0
          }
        },
        yAxis: yAxisOption,
        series: [
          {
            name: config.name,
            type: "bar",
            data,
            itemStyle:
              config.barColor && typeof config.barColor === "function"
                ? {
                  color: (params) => {
                    const v = params.value;
                    return v != null ? config.barColor(v) : "#C0C4CC";
                  }
                }
                : { color: "#409EFF" }
          }
        ]
      };
    }
  },
  watch: {
    chargeInfos: {
      handler() {
        this.$nextTick(() => {
          if (this.chart && this.$refs.chargeChart) {
            this.$refs.chargeChart.setOption(this.chartOption);
          }
        });
      },
      deep: true
    }
  },
  methods: {
    onChartLoad(chart) {
      this.chart = chart;
      if (this.chargeInfos && this.chargeInfos.length > 0) {
        this.$nextTick(() => {
          if (this.$refs.chargeChart) {
            this.$refs.chargeChart.setOption(this.chartOption);
          }
        });
      }
    },
    handleTabClick() {
      this.$nextTick(() => {
        if (this.$refs.chargeChart && this.chartOption) {
          this.$refs.chargeChart.setOption(this.chartOption);
        }
      });
    }
  }
};
</script>

<style lang="less" scoped>
.charge-chart {
  &.with-card {
    margin-top: 10px;
  }

  /deep/ .el-card__body {
    padding: 15px;
  }

  .chart-tabs {
    margin-bottom: 12px;
  }

  /deep/ .el-tabs__header {
    margin-bottom: 12px;
  }

  /deep/ .el-tabs__item {
    padding: 0 16px;
    height: 36px;
    line-height: 36px;
  }

  /deep/ .el-tabs__content {
    overflow: visible;
  }
}
</style>
